/**
 * Templates tab insertion (S7) — split out of AddTileDialog to keep it from
 * growing further past its already-over-200-lines footprint (S6 deviation).
 * Inserting a template writes its SQL as EDITABLE text, pre-fits the tile
 * type (+ stat value column), prefills the title, drops the dialog back onto
 * the SQL tab (teaching surface — nothing hides how the tile actually
 * works), and auto-runs the preview. The category is tracked separately from
 * `activeSource` because the switch back to 'sql' happens immediately —
 * `activeSource` alone can't tell the submit handler the SQL came from a
 * template (analytics provenance).
 */
import { useState } from 'react';
import type { AddTileFormValues } from './addTileFormModel';
import type { AddTileSource } from './AddTileDataPane';
import type { TilePreview } from './useTilePreview';
import type { DashboardTemplate } from '../../templates/templateTypes';

export type TemplateInsertionContext = {
  set: (patch: Partial<AddTileFormValues>) => void;
  setSql: (sql: string) => void;
  setActiveSource: (source: AddTileSource) => void;
  preview: TilePreview;
  connectionId: string;
  database: string | undefined;
  dashboardId: string;
};

export function useTemplateInsertion() {
  const [templateCategoryUsed, setTemplateCategoryUsed] = useState<
    DashboardTemplate['category'] | null
  >(null);

  const reset = () => setTemplateCategoryUsed(null);

  const insertTemplate = (template: DashboardTemplate, ctx: TemplateInsertionContext) => {
    ctx.setSql(template.sql);
    ctx.set({
      tileType: template.defaultTileType,
      title: template.title,
      emptyStateCopy: template.emptyStateCopy,
      ...(template.statValueColumn ? { statValueColumn: template.statValueColumn } : {}),
    });
    setTemplateCategoryUsed(template.category);
    ctx.setActiveSource('sql');
    ctx.preview.run({
      connectionId: ctx.connectionId,
      database: ctx.database,
      sql: template.sql,
      dashboardId: ctx.dashboardId,
    });
  };

  return { templateCategoryUsed, reset, insertTemplate };
}
